import { useState, useRef, useEffect } from 'react';

interface MusicTrack {
  id: string;
  title: string;
  src: string;
}

const hairWorks = [
  { image: 'https://static.readdy.ai/image/6902dd986b6e328bf9bb68047035d84f/260097b3e2be96d327f825945f63b702.png' },
  { image: 'https://static.readdy.ai/image/6902dd986b6e328bf9bb68047035d84f/b2aade39752a28ffa7de6dce48cebe0d.png' },
  { image: 'https://static.readdy.ai/image/6902dd986b6e328bf9bb68047035d84f/7d093a1ca39a6a89042d5f9dddeb56e5.jpeg' },
  { image: 'https://static.readdy.ai/image/6902dd986b6e328bf9bb68047035d84f/654b0e57566ad9f2886262b0b3d46d0f.png' },
  { image: 'https://static.readdy.ai/image/6902dd986b6e328bf9bb68047035d84f/bb310e43727b3a85ba899baea7abcf51.png' },
  { image: 'https://static.readdy.ai/image/6902dd986b6e328bf9bb68047035d84f/fbef6af63f5c98380a34793a0d9f05dd.png' },
  { image: 'https://storage.readdy-site.link/project_files/38ba0d2e-b3d0-4053-be77-c6b79537f441/f4597f0c-2e24-4d39-bef6-d693daf44741_-2026-03-29-4.02.34.png?v=468c1770d32f1baef6ab32eb121d7bb7' },
  { image: 'https://static.readdy.ai/image/6902dd986b6e328bf9bb68047035d84f/cc73411c282710df8536a8e06fdf0852.png' },
];

const musicWorks: MusicTrack[] = [
  {
    id: 'incwave_sample1',
    title: 'incwave_sample1',
    src: 'https://storage.readdy-site.link/project_files/38ba0d2e-b3d0-4053-be77-c6b79537f441/342c16e5-8049-40b6-b268-114112bb421a_ShojiLight.mp3?v=36906aa36612b6c1700115b6777ce25b',
  },
  {
    id: 'incwave_sample2',
    title: 'incwave_sample2',
    src: 'https://storage.readdy-site.link/project_files/38ba0d2e-b3d0-4053-be77-c6b79537f441/de942760-044e-42d2-8257-df0a32402998_FATAL.mp3?v=0fa1319df28e71c5982c2b1b0a403457',
  },
];

const WAVEFORM_HEIGHTS = [20, 35, 55, 70, 45, 80, 60, 90, 50, 75, 40, 65, 85, 30, 95, 55, 70, 45, 80, 60, 90, 50, 75, 40, 65, 85, 30, 95, 55, 70, 45, 80, 60, 90, 50, 75, 40, 65, 30, 20];

function AudioPlayer({ track }: { track: MusicTrack }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => {
      if (!isDragging) setCurrentTime(audio.currentTime);
    };
    const onLoadedMetadata = () => setDuration(audio.duration);
    const onEnded = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('ended', onEnded);

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('ended', onEnded);
    };
  }, [isDragging]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const formatTime = (t: number) => {
    if (!isFinite(t)) return '0:00';
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value);
    setCurrentTime(val);
    if (audioRef.current) audioRef.current.currentTime = val;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="bg-slate-800/70 border border-white/10 hover:border-white/20 rounded-2xl p-8 transition-all duration-500">
      <audio ref={audioRef} src={track.src} preload="metadata" />

      {/* Waveform visual */}
      <div className="flex items-end gap-[3px] h-10 mb-8">
        {WAVEFORM_HEIGHTS.map((h, i) => {
          const filled = progress > 0 && (i / WAVEFORM_HEIGHTS.length) * 100 < progress;
          return (
            <div
              key={i}
              className={`flex-1 rounded-full transition-colors duration-300 ${filled ? 'bg-yellow-400/80' : 'bg-yellow-400/15'}`}
              style={{ height: `${h}%` }}
            />
          );
        })}
      </div>

      {/* Title */}
      <div className="mb-6">
        <p className="text-xs tracking-[0.3em] text-gray-500 uppercase mb-1">Music Works</p>
        <h4 className="text-xl font-light tracking-widest text-white">{track.title}</h4>
      </div>

      {/* Seek bar */}
      <div className="mb-6">
        <input
          type="range"
          min={0}
          max={duration || 100}
          value={currentTime}
          onChange={handleSeek}
          onMouseDown={() => setIsDragging(true)}
          onMouseUp={() => setIsDragging(false)}
          className="w-full h-[2px] appearance-none rounded-full cursor-pointer"
          style={{
            background: `linear-gradient(to right, rgba(255,255,255,0.85) ${progress}%, rgba(255,255,255,0.12) ${progress}%)`
          }}
        />
        <div className="flex justify-between mt-2">
          <span className="text-xs text-gray-500 font-mono">{formatTime(currentTime)}</span>
          <span className="text-xs text-gray-500 font-mono">{formatTime(duration)}</span>
        </div>
      </div>

      {/* Play / Pause button */}
      <div className="flex items-center justify-center">
        <button
          onClick={togglePlay}
          className="w-14 h-14 flex items-center justify-center rounded-full border border-yellow-400/60 hover:border-yellow-300 hover:bg-yellow-400/10 transition-all duration-300 cursor-pointer"
        >
          <div className="w-14 h-14 flex items-center justify-center">
            <i className={`${isPlaying ? 'ri-pause-line' : 'ri-play-line'} text-2xl text-yellow-400`}></i>
          </div>
        </button>
      </div>
    </div>
  );
}

export default function Gallery() {
  return (
    <section id="gallery" className="py-24 px-6 bg-gradient-to-b from-black via-slate-900/60 to-black">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 tracking-wider">GALLERY</h2>
          <div className="w-24 h-1 bg-purple-500 mx-auto"></div>
        </div>

        {/* Hair & Makeup Works */}
        <div className="mb-20">
          <h3 className="text-2xl font-bold mb-8 tracking-wider flex items-center gap-3">
            <i className="ri-scissors-cut-line text-purple-400"></i>
            Hair &amp; Makeup
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {hairWorks.map((work, index) => (
              <div
                key={index}
                className="group overflow-hidden rounded-lg border border-purple-500/20 hover:border-purple-500/60 transition-all duration-500 cursor-pointer"
              >
                <div className="h-48 md:h-96 overflow-hidden bg-slate-800/40 flex items-center justify-center">
                  {work.image ? (
                    <img
                      src={work.image}
                      alt=""
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700 select-none pointer-events-none"
                      draggable={false}
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-3 text-purple-500/40">
                      <div className="w-12 h-12 flex items-center justify-center">
                        <i className="ri-image-add-line text-4xl"></i>
                      </div>
                      <span className="text-sm tracking-widest">PHOTO</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Music Works */}
        <div>
          <h3 className="text-2xl font-bold mb-8 tracking-wider flex items-center gap-3">
            <i className="ri-music-2-line text-purple-400"></i>
            Music Works
          </h3>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {musicWorks.map((track) => (
              <AudioPlayer key={track.id} track={track} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
